import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { TrendingUp, Download, Calendar } from "lucide-react";
import * as XLSX from "xlsx";

interface Invoice {
  invoice_date: string;
  amount: number;
  service_name: string;
  patient_name: string;
}

interface GiftCardSale {
  created_at: string;
  amount: number;
}

type ChartType = "bar" | "line" | "pie";
type TimeRange = "month" | "quarter" | "year" | "all";

export const BillingAnalytics: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [giftCardSales, setGiftCardSales] = useState<GiftCardSale[]>([]);
  const [chartType, setChartType] = useState<ChartType>("bar");
  const [timeRange, setTimeRange] = useState<TimeRange>("month");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      // Load invoices
      const { data: invoiceData, error: invoiceError } = await supabase
        .from("invoices")
        .select(`
          invoice_date,
          amount,
          service:services(name),
          patient:patients(name)
        `)
        .eq("user_id", user.id)
        .order("invoice_date", { ascending: true });

      if (invoiceError) throw invoiceError;

      const transformedInvoices = invoiceData?.map(inv => ({
        invoice_date: inv.invoice_date,
        amount: inv.amount,
        service_name: (Array.isArray(inv.service) ? inv.service[0]?.name : inv.service?.name) || "N/A",
        patient_name: (Array.isArray(inv.patient) ? inv.patient[0]?.name : inv.patient?.name) || "N/A",
      })) || [];

      setInvoices(transformedInvoices);

      // Load gift card sales (only paid ones)
      const { data: giftCardData, error: giftCardError } = await supabase
        .from("gift_cards")
        .select("created_at, amount")
        .eq("status", "paid")
        .order("created_at", { ascending: true });

      if (giftCardError) throw giftCardError;
      setGiftCardSales(giftCardData || []);
    } catch (error: any) {
      console.error("Erreur chargement données:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const getFilteredData = () => {
    const now = new Date();
    let startDate = new Date();

    switch (timeRange) {
      case "month":
        startDate.setMonth(now.getMonth() - 1);
        break;
      case "quarter":
        startDate.setMonth(now.getMonth() - 3);
        break;
      case "year":
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      case "all":
        return invoices;
    }

    return invoices.filter(inv => new Date(inv.invoice_date) >= startDate);
  };

  const getFilteredGiftCards = () => {
    const now = new Date();
    let startDate = new Date();

    switch (timeRange) {
      case "month":
        startDate.setMonth(now.getMonth() - 1);
        break;
      case "quarter":
        startDate.setMonth(now.getMonth() - 3);
        break;
      case "year":
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      case "all":
        return giftCardSales;
    }

    return giftCardSales.filter(gc => new Date(gc.created_at) >= startDate);
  };

  const getRevenueByMonth = () => {
    const filteredInvoices = getFilteredData();
    const filteredGiftCards = getFilteredGiftCards();
    const monthlyData: { [key: string]: { invoices: number; giftCards: number } } = {};

    filteredInvoices.forEach(inv => {
      const monthKey = new Date(inv.invoice_date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short'
      });
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { invoices: 0, giftCards: 0 };
      }
      monthlyData[monthKey].invoices += inv.amount;
    });

    filteredGiftCards.forEach(gc => {
      const monthKey = new Date(gc.created_at).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short'
      });
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { invoices: 0, giftCards: 0 };
      }
      monthlyData[monthKey].giftCards += gc.amount;
    });

    return Object.entries(monthlyData).map(([month, data]) => ({
      month,
      factures: parseFloat(data.invoices.toFixed(2)),
      cartesCadeaux: parseFloat(data.giftCards.toFixed(2)),
      total: parseFloat((data.invoices + data.giftCards).toFixed(2))
    }));
  };

  const getRevenueByService = () => {
    const filtered = getFilteredData();
    const filteredGiftCards = getFilteredGiftCards();
    const serviceData: { [key: string]: number } = {};

    filtered.forEach(inv => {
      serviceData[inv.service_name] = (serviceData[inv.service_name] || 0) + inv.amount;
    });

    // Add gift cards as a separate category
    const giftCardTotal = filteredGiftCards.reduce((sum, gc) => sum + gc.amount, 0);
    if (giftCardTotal > 0) {
      serviceData["Cartes Cadeaux"] = giftCardTotal;
    }

    return Object.entries(serviceData).map(([service, revenue]) => ({
      service,
      revenue: parseFloat(revenue.toFixed(2))
    }));
  };

  const exportAnalyticsToExcel = () => {
    const monthlyData = getRevenueByMonth();
    const serviceData = getRevenueByService();
    const filtered = getFilteredData();

    const wb = XLSX.utils.book_new();

    // Sheet 1: Revenue by month
    const ws1 = XLSX.utils.json_to_sheet(monthlyData);
    XLSX.utils.book_append_sheet(wb, ws1, "Revenus mensuels");

    // Sheet 2: Revenue by service
    const ws2 = XLSX.utils.json_to_sheet(serviceData);
    XLSX.utils.book_append_sheet(wb, ws2, "Revenus par service");

    // Sheet 3: All invoices
    const detailedData = filtered.map(inv => ({
      Date: new Date(inv.invoice_date).toLocaleDateString('fr-FR'),
      Patient: inv.patient_name,
      Service: inv.service_name,
      "Montant (€)": inv.amount.toFixed(2),
    }));
    const ws3 = XLSX.utils.json_to_sheet(detailedData);
    XLSX.utils.book_append_sheet(wb, ws3, "Détails factures");

    XLSX.writeFile(wb, `analytique-comptable-${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Export Excel réussi");
  };

  const monthlyData = getRevenueByMonth();
  const serviceData = getRevenueByService();
  const invoiceTotal = getFilteredData().reduce((sum, inv) => sum + inv.amount, 0);
  const giftCardTotal = getFilteredGiftCards().reduce((sum, gc) => sum + gc.amount, 0);
  const totalRevenue = invoiceTotal + giftCardTotal;
  const totalTransactions = getFilteredData().length + getFilteredGiftCards().length;
  const averageTransaction = totalRevenue / (totalTransactions || 1);

  const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))', 'hsl(142 76% 36%)'];

  return (
    <Card className="shadow-card bg-gradient-card border-0">
      <CardHeader className="border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl font-playfair text-primary flex items-center gap-2">
              <TrendingUp className="w-6 h-6" />
              Analytique comptable
            </CardTitle>
            <CardDescription>
              Visualisez vos revenus et performances
            </CardDescription>
          </div>
          <Button
            onClick={exportAnalyticsToExcel}
            disabled={invoices.length === 0}
            className="gap-2"
          >
            <Download className="w-4 h-4" />
            Exporter Excel
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6 pt-6">
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Type de graphique</Label>
            <Select value={chartType} onValueChange={(value) => setChartType(value as ChartType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bar">Graphique en barres</SelectItem>
                <SelectItem value="line">Graphique en courbes</SelectItem>
                <SelectItem value="pie">Graphique circulaire</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Période</Label>
            <Select value={timeRange} onValueChange={(value) => setTimeRange(value as TimeRange)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Dernier mois</SelectItem>
                <SelectItem value="quarter">Dernier trimestre</SelectItem>
                <SelectItem value="year">Dernière année</SelectItem>
                <SelectItem value="all">Tout</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-primary/5 border-primary/20">
            <CardHeader className="pb-2">
              <CardDescription>Revenu total</CardDescription>
              <CardTitle className="text-3xl font-bold text-primary">
                {totalRevenue.toFixed(2)}€
              </CardTitle>
            </CardHeader>
          </Card>

          <Card className="bg-secondary/5 border-secondary/20">
            <CardHeader className="pb-2">
              <CardDescription>Cartes Cadeaux</CardDescription>
              <CardTitle className="text-3xl font-bold text-secondary">
                {giftCardTotal.toFixed(2)}€
              </CardTitle>
            </CardHeader>
          </Card>

          <Card className="bg-accent/5 border-accent/20">
            <CardHeader className="pb-2">
              <CardDescription>Transactions</CardDescription>
              <CardTitle className="text-3xl font-bold text-accent">
                {totalTransactions}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Charts */}
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">
            Chargement des données...
          </div>
        ) : invoices.length === 0 && giftCardSales.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Aucune donnée disponible
          </div>
        ) : (
          <div className="space-y-6">
            {/* Revenue Over Time */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Évolution du chiffre d'affaires</h3>
              <ResponsiveContainer width="100%" height={300}>
                {chartType === "bar" ? (
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" stroke="hsl(var(--foreground))" />
                    <YAxis stroke="hsl(var(--foreground))" />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Bar 
                      dataKey="factures" 
                      name="Factures (€)" 
                      fill="hsl(var(--primary))"
                      animationDuration={1000}
                      stackId="revenue"
                    />
                    <Bar 
                      dataKey="cartesCadeaux" 
                      name="Cartes Cadeaux (€)" 
                      fill="hsl(var(--secondary))"
                      animationDuration={1000}
                      stackId="revenue"
                    />
                  </BarChart>
                ) : (
                  <LineChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" stroke="hsl(var(--foreground))" />
                    <YAxis stroke="hsl(var(--foreground))" />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="total"
                      name="Revenu (€)" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={2}
                      animationDuration={1000}
                    />
                  </LineChart>
                )}
              </ResponsiveContainer>
            </Card>

            {/* Revenue by Service */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Répartition par service</h3>
              <ResponsiveContainer width="100%" height={300}>
                {chartType === "pie" ? (
                  <PieChart>
                    <Pie
                      data={serviceData}
                      dataKey="revenue"
                      nameKey="service"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={(entry) => `${entry.service}: ${entry.revenue}€`}
                      animationDuration={1000}
                    >
                      {serviceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                ) : (
                  <BarChart data={serviceData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" stroke="hsl(var(--foreground))" />
                    <YAxis dataKey="service" type="category" stroke="hsl(var(--foreground))" width={150} />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Bar 
                      dataKey="revenue" 
                      name="Revenu (€)" 
                      fill="hsl(var(--secondary))"
                      animationDuration={1000}
                    />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </Card>
          </div>
        )}
      </CardContent>
    </Card>
  );
};